import type { BuyInStore } from '#types/buyInStore'
import { skipHydrate } from 'pinia'

/**
 * Buy In Store Store - manages in-store employee sessions and store associate logins.
 *
 * **Key behaviors:**
 * - Stores employee/store information in sessionStorage (not persisted long-term)
 * - Prevents hydration issues with `skipHydrate` (session data is client-only)
 * - Integrates with auth store for logout flow
 * - Redirects to `/associate-login` after session clear
 *
 * **Use cases:**
 * - Store associates helping customers (POS integration)
 * - In-store kiosk sessions
 * - Employee discount validation
 *
 * **Multi-brand notes:**
 * - Used by all brands (TNF, Timberland, Vans, Canvas)
 * - Disables certain features when employee connected (e.g., favorites)
 * - Employee discount T&C acceptance tracked separately
 *
 * **Security considerations:**
 * - 🔒 Session data stored client-side only (no server persistence)
 * - 🔒 Cleared on logout to prevent session leakage
 * - 🔒 Always validate employee permissions server-side
 *
 * @see domains/commerce/stores/auth.ts for login/logout integration
 * @see domains/commerce/middleware/associate-auth.ts for route protection
 * @see #types/buyInStore for BuyInStore interface definition
 */
export const useBuyInStoreStore = defineStore('buyInStore', () => {
  const auth = useAuthStore()
  const router = useRouter()

  // ─────────────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────────────

  /** Default empty store info structure */
  const emptyStoreInfo = { storeId: '', storeName: '', storeEmployeeId: '', storeEmployeeName: '' }

  /**
   * Store and employee session information.
   *
   * **Stored in sessionStorage** (survives page refresh, not browser restart)
   * **skipHydrate**: Prevents SSR/client mismatch (session data only exists client-side)
   *
   * @type {BuyInStore} Contains storeId, storeName, storeEmployeeId, storeEmployeeName
   */
  const storeInfo = skipHydrate(
    useSessionStorage<BuyInStore>('buyInStore', { ...emptyStoreInfo })
  )

  /**
   * Computed: Is a store employee currently connected?
   *
   * **Returns true if:**
   * - storeEmployeeId is set AND
   * - storeId is set
   *
   * **skipHydrate**: Prevents SSR rendering (always undefined on server)
   *
   * **Used by:**
   * - Favorites store (disable favorites for employees)
   * - Cart store (apply store employee discounts)
   * - Auth middleware (control access to employee-only routes)
   */
  const employeeConnected = skipHydrate(computed(() => !!(storeInfo.value.storeEmployeeId && storeInfo.value.storeId)))

  // ─────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Set store information (called after associate login).
   *
   * **Behavior:**
   * - Merges provided data with emptyStoreInfo defaults
   * - Updates sessionStorage automatically (via useSessionStorage)
   *
   * @param {BuyInStore} storeData - Store and employee info from POS login
   *
   * @example
   * ```ts
   * // After successful POS login
   * const bopisStore = useBuyInStoreStore()
   * bopisStore.setStoreInfo({
   *   storeId: 'STORE-001',
   *   storeName: 'Denver Flagship',
   *   storeEmployeeId: 'EMP-12345',
   *   storeEmployeeName: 'John Doe'
   * })
   * ```
   */
  const setStoreInfo = (storeData: BuyInStore) => {
    storeInfo.value = { ...emptyStoreInfo, ...storeData }
  }

  /**
   * Clear employee session and logout.
   *
   * **Behavior:**
   * 1. Clears sessionStorage store info (client-side only)
   * 2. Calls auth.logout() to clear tokens/cookies
   * 3. Redirects to `/associate-login` page
   *
   * **Error handling:**
   * - Logs logout errors (does not throw)
   * - Always redirects even if logout fails (failsafe)
   *
   * ⚠️ **Always navigates to /associate-login** (prevents stale session access)
   *
   * @example
   * ```ts
   * // User clicks "End Session" button
   * const bopisStore = useBuyInStoreStore()
   * await bopisStore.clearSession()
   * // → User is now at /associate-login
   * ```
   */
  const clearSession = async () => {
    if (isClient())
      storeInfo.value = { ...emptyStoreInfo }
    try {
      await auth.logout()
    }
    catch (err) {
      log.error(`Error logging out a store associate: ${err}`)
    }
    finally {
      isClient() && router.replace({ path: '/associate-login' })
    }
  }

  return {
    employeeConnected,
    storeInfo,
    setStoreInfo,
    clearSession
  }
})
